class SizeCalculator extends HTMLElement {
    constructor() {
        super();
        this.mapping = [];
    }

    connectedCallback() {
        const sizeMapping = this.dataset.sizeMapping;
        if (sizeMapping) {
            this.mapping = JSON.parse(decodeURIComponent(sizeMapping));
            console.table(this.mapping,);
            this.removeAttribute('data-size-mapping');
        }

        this.size1 = this.querySelector("[id$='-size1']");
        this.size2 = this.querySelector("[id$='-size2']");
        this.size1Error = this.querySelector(".input-feedback[labelledby$='-size1']");
        this.size2Error = this.querySelector(".input-feedback[labelledby$='-size2']");
        this.result = this.querySelector(".size-result");
        this.extraResult = this.querySelector(".size-result-extra");
        const calculateButton = this.querySelector(".calculate-size-button");
        if (calculateButton) {
            calculateButton.addEventListener("click", this.calculate.bind(this));
        }
        this.minSize1 = this.mapping.reduce((min, mapping) => Math.min(min, mapping.min_size_1), Infinity);
        this.maxSize1 = this.mapping.reduce((max, mapping) => Math.max(max, mapping.max_size_1), 0);
        this.minSize2 = this.mapping.reduce((min, mapping) => Math.min(min, mapping.min_size_2), Infinity);
        this.maxSize2 = this.mapping.reduce((max, mapping) => Math.max(max, mapping.max_size_2), 0);

        const internationalConvertTableLink = document.querySelector('.template-page .size-chart-description a');
        if (internationalConvertTableLink) {
            internationalConvertTableLink.addEventListener("click", (e) => {
                e.preventDefault();
                const targetId = e.currentTarget.getAttribute('href')?.slice(1);
                if (!targetId) return;

                const targetTable = document.getElementById(targetId);
                if (!targetTable) return;

                const header = document.querySelector('sticky-header');
                const headerHeight = header?.offsetHeight ?? 0;
                const offsetPosition = targetTable.getBoundingClientRect().top + window.scrollY - headerHeight;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            });
        }
    }

    calculate() {

        this.size1.classList.remove('is-invalid');
        this.size1Error.textContent = '';
        this.size2.classList.remove('is-invalid');
        this.size2Error.textContent = '';

        let size1 = +this.size1.value;
        let size2 = +this.size2.value;
        let factor = 1;
        
        const unit = this.querySelector('.unit-toggle button.selected').dataset.option;

        if (unit === 'in') {
            factor = 2.54;
        }

        let minSize1 = (this.minSize1 / factor).toFixed(1);
        let maxSize1 = (this.maxSize1 / factor).toFixed(1);
        let minSize2 = (this.minSize2 / factor).toFixed(1);
        let maxSize2 = (this.maxSize2 / factor).toFixed(1);
       
        let skip = false;
        // Validate size1
        if (size1 === undefined || isNaN(size1)) {
            this.size1Error.textContent = this.size1Error.dataset.invalidValueMessage || 'Invalid value';
            this.size1.classList.add('is-invalid');
            skip = true;
        } else if (!Number.isInteger(size1) && unit === 'cm') {
            this.size1Error.textContent = this.size1Error.dataset.invalidTypeMessage || 'Value must be an integer';
            this.size1.classList.add('is-invalid');
            skip = true;
        }

        if (!skip) { 
            if (size1 < minSize1 || size1 > maxSize1) {
                this.size1Error.textContent = this.size1Error.dataset.invalidValueMessage || 'Value out of range';
                this.size1.classList.add('is-invalid');
                skip = true;
            } else  {
                this.size1Error.textContent = '';
                this.size1.classList.remove('is-invalid');
            }
        }

        // Validate size2
        if (size2 === undefined || isNaN(size2)) {
            this.size2Error.textContent = this.size2Error.dataset.invalidValueMessage || 'Invalid value';
            this.size2.classList.add('is-invalid');
            skip = true;
        } else if (!Number.isInteger(size2) && unit === 'cm') {
            this.size2Error.textContent = this.size2Error.dataset.invalidTypeMessage || 'Value must be an integer';
            this.size2.classList.add('is-invalid');
            skip = true;
        }

        if (!skip) {
            if (size2 < minSize2 || size2 > maxSize2) {
                this.size2Error.textContent = this.size2Error.dataset.invalidValueMessage || 'Value out of range';
                this.size2.classList.add('is-invalid');
                skip = true;
            } else {
                this.size2Error.textContent = '';
                this.size2.classList.remove('is-invalid');
            }
        }

        if (skip) {
            this.result.innerHTML = '';
            this.extraResult.innerHTML = '';
            return;
        }
        const sizeMapping = this.calculateSize(size1, size2, unit);
        if (sizeMapping) {
            this.result.innerHTML = sizeMapping.size;
            if (sizeMapping.extra_size) {
                this.extraResult.innerHTML = sizeMapping.extra_size;
            } else {
                this.extraResult.innerHTML = '';
            }
            
            this.size1.classList.remove('is-invalid');
            this.size2.classList.remove('is-invalid');
        } else {
            this.size2Error.textContent = this.size2Error.dataset.noMeasurementsErrorMessage || 'We can’t seem to find a size for those measurements; please try adding in different measurements';
            this.result.innerHTML = '';
            this.extraResult.innerHTML = '';
            this.size1.classList.add('is-invalid');
            this.size2.classList.add('is-invalid');
        }
    }

    calculateSize(size1, size2, unit) {
        let factor = 1;
        if (unit === 'in') {
            factor = 2.54;
        }
        const size = parseFloat(size1);
        for (let i = 0; i < this.mapping.length; i++) {
            const mapping = this.mapping[i];
            let min_size_1 = (mapping.min_size_1 / factor).toFixed(1);
            let max_size_1 = (mapping.max_size_1 / factor).toFixed(1);
            let min_size_2 = (mapping.min_size_2 / factor).toFixed(1);
            let max_size_2 = (mapping.max_size_2 / factor).toFixed(1);
            if (size >= min_size_1 && size <= max_size_1 && size2 >= min_size_2 && size2 <= max_size_2) {
                return mapping;
            }
        }
        return ;
    }
}

class SizeChartTable extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.size1Title = this.dataset.size1Title || 'Size 1';
        this.size2Title = this.dataset.size2Title || 'Size 2';
        this.unit = this.dataset.unit || 'cm';
        const mapping = this.dataset.sizeMapping;
        if ( mapping ) {
            this.mapping = JSON.parse(decodeURIComponent(mapping));
            this.removeAttribute('data-size-mapping');
        }
        this.populateSizeMapping();
    }

    populateSizeMapping() {
        let factor = 1;
        if (this.unit === 'in') {
            factor = 2.54;
        }

        const mapping = this.mapping
            .sort((a, b) => a.min_size_1 - b.min_size_1 || a.min_size_2 - b.min_size_2)
            .map(map => {
                return {
                    size1_header: this.unit === 'in' ? `${(map.min_size_1 / factor).toFixed(1)} - ${(map.max_size_1 / factor).toFixed(1)}` : `${Math.ceil(map.min_size_1)} - ${Math.ceil(map.max_size_1)}`,
                    size2_header: this.unit === 'in' ? `${(map.min_size_2 / factor).toFixed(1)} - ${(map.max_size_2 / factor).toFixed(1)}` : `${Math.ceil(map.min_size_2)} - ${Math.ceil(map.max_size_2)}`,
                    min_size_1: map.min_size_1,
                    max_size_1: map.max_size_1,
                    min_size_2: map.min_size_2,
                    max_size_2: map.max_size_2,
                    size: map.size,
                    extra_size: map.extra_size
                };
            });

        const table = document.createElement('div');
        table.classList.add('table-container');
        if (this.unit === 'in') {
            table.classList.add('wrap-header');
        }
        
        const leftPanel = document.createElement('div');
        leftPanel.classList.add('left-panel');
        const size2TitleCell = document.createElement('div');
        size2TitleCell.classList.add('size-2-title');

        const container = document.createElement('div');
        container.classList.add('container');
        const rotateText = document.createElement('div');
        rotateText.classList.add('rotate-text');
        rotateText.textContent = this.size2Title;
        container.appendChild(rotateText);
        size2TitleCell.appendChild(container);
        leftPanel.appendChild(size2TitleCell);

        const size2RangesContainer = document.createElement('div');
        size2RangesContainer.classList.add('size-2-range');

        let size2s = mapping.map(map => map.size2_header);
        size2s = [...new Set(size2s)];
        size2s.forEach((size_2, index) => {
            const size2Range = document.createElement('div');
            size2Range.classList.add('header-cell');
            size2Range.classList.add('size-header-cell');
            size2Range.classList.add('cell');
            size2Range.textContent = size_2 + this.unit;
            size2RangesContainer.appendChild(size2Range);
        });
        leftPanel.appendChild(size2RangesContainer);
        table.appendChild(leftPanel);

        const rightPanel = document.createElement('div');
        rightPanel.classList.add('right-panel');

        const size1RangesHeaderContainer = document.createElement('div');
        size1RangesHeaderContainer.classList.add('size-1-range');
        size1RangesHeaderContainer.classList.add('row');

        let size1s = mapping.map(map => map.size1_header);
        size1s = [...new Set(size1s)];
        size1s.forEach((size_1, index) => {
            const size1Range = document.createElement('div');
            size1Range.classList.add('header-cell');
            size1Range.classList.add('size-header-cell');
            size1Range.classList.add('cell');
            size1Range.textContent = size_1 + this.unit;
            size1RangesHeaderContainer.appendChild(size1Range);
        });
        rightPanel.appendChild(size1RangesHeaderContainer);
        size2s.forEach((map, index) => {
            const valueRow = document.createElement('div');
            valueRow.classList.add('size-data-row');
            valueRow.classList.add('row');
            size1s.forEach((size_1, colIndex) => {
                const map = mapping.find(map => map.size1_header === size_1 && map.size2_header === size2s[index]);
                const dataCell = document.createElement('div');
                dataCell.classList.add('size-data-cell');
                dataCell.classList.add('cell');
                if (map) {
                    const validDiv1 = document.createElement('div');
                    validDiv1.classList.add('valid');
                    validDiv1.textContent = map.extra_size;
                    const validDiv2 = document.createElement('div');
                    validDiv2.classList.add('valid');
                    validDiv2.textContent = map.size;
                    dataCell.appendChild(validDiv1);
                    dataCell.appendChild(validDiv2);
                } else {
                    const span = document.createElement('span');
                    dataCell.appendChild(span);
                }
                valueRow.appendChild(dataCell);
            });
            rightPanel.appendChild(valueRow);
        });
        table.appendChild(rightPanel);
        const size_1_title = document.createElement('div');
        size_1_title.classList.add('size-1-title');
        size_1_title.textContent = this.size1Title;
        
        this.innerHTML = '';
        this.appendChild(size_1_title);
        this.appendChild(table);
    }
}

class FitGuide extends HTMLElement {
    constructor() {
        super();
        this.sizeChartTable = this.querySelector('size-chart-table');
    }

    connectedCallback() {
        this.unit = this.querySelector('.unit-toggle button.selected').dataset.option;
        const buttonsToggle = this.querySelectorAll('.unit-toggle');
        buttonsToggle.forEach((button) => {
            button.addEventListener('amb-button-group:changed', e => {
                const selectedIndex = e.detail.selectedIndex;
                buttonsToggle.forEach(btn => {
                    btn.select(selectedIndex, false);
                })
                this.sizeChartTable.unit = e.detail.selected.dataset.option;
                this.sizeChartTable.populateSizeMapping();
            });
        });
    }
}


if (!customElements.get("size-calculator")) {
    customElements.define("size-calculator", SizeCalculator);
}

if (!customElements.get("size-chart-table")) {
    customElements.define("size-chart-table", SizeChartTable);
}

if (!customElements.get("fit-guide")) {
    customElements.define("fit-guide", FitGuide);
}
